import { nextTick } from 'vue'
import { formatQueryError } from '@zoryn/components/grid/src/api/format-query-error'
import {
  applyQueryPaginationOverride,
  mergeGridQueryParams,
} from '@zoryn/components/grid/src/utils/append-extra-query-params'
import { resolveGridRequest } from '@zoryn/components/grid/src/utils/resolve-grid-request'
import type { GridQueryParams } from '@zoryn/components/grid'
import {
  parsePageResult,
  resolveModuleUrls,
  type BaseControllerPageResult,
} from '../api/api-convention'
import { initExpandSetFromFlat, syncTreeTableData } from '../tree/sync-table-data'
import type { DataGridTree } from '../types'

export function getGridTreeOptions($grid: DataGridTree) {
  return $grid.options
}

export async function reloadTreeData($grid: DataGridTree) {
  await $grid.queryByExamplePage()
}

export function useQuery($grid: DataGridTree) {
  let querySeq = 0

  const applyFlatList = async (list: Record<string, unknown>[]) => {
    const prevCurrentId = $grid.reactData.currentRow?.id
    $grid.reactData.editStr = ''
    $grid.reactData.editStore.actived = { row: null, field: null }
    $grid.internalData.flatSource = [...list]
    initExpandSetFromFlat($grid)
    syncTreeTableData($grid)
    $grid.updateFooter?.()

    const targetRow =
      prevCurrentId != null
        ? $grid.reactData.tableData.find((item) => item.id === prevCurrentId)
          ?? $grid.reactData.tableData[0]
          ?? null
        : $grid.reactData.tableData[0] ?? null

    await nextTick()
    await $grid.scrollToTop?.()
    $grid.setCurrentRow(targetRow ?? null)
    $grid.recalculateColumnWidths?.($grid.bodyRef?.value?.clientWidth)
  }

  const queryByExamplePage = async (overrideParams?: GridQueryParams) => {
    const seq = ++querySeq
    const { reactData } = $grid
    const options = getGridTreeOptions($grid)
    const urls = resolveModuleUrls(options.module)
    const url = options.queryUrl || urls.queryByExamplePage

    const loadAll = options.loadAll !== false
    const params: Record<string, unknown> = {
      currentPage: reactData.currentPage,
      pageSize: reactData.pageSize,
      pageFlag: loadAll,
      filters: [] as unknown[],
    }
    mergeGridQueryParams(params, options.params, overrideParams)
    applyQueryPaginationOverride(reactData, params, overrideParams)
    options.beforeQuery?.(params)

    if ((params.filters as unknown[]).length > 0) {
      params.filters = JSON.stringify(params.filters)
    } else {
      delete params.filters
    }

    reactData.loading = true
    reactData.loadError = false

    try {
      const request = resolveGridRequest($grid)
      const res = await request.get<BaseControllerPageResult<Record<string, unknown>[]>>({
        url,
        params,
        meta: { skipErrorHandler: true },
      })
      if (seq !== querySeq) {
        return
      }
      const { list, total } = parsePageResult(res)
      if (loadAll) {
        reactData.currentPage = 1
      } else {
        reactData.total = total
      }
      await applyFlatList(list)
    } catch (e: unknown) {
      if (seq !== querySeq) {
        return
      }
      reactData.loadError = true
      reactData.loadErrorMsg = formatQueryError(e)
      throw e
    } finally {
      if (seq === querySeq) {
        reactData.loading = false
      }
    }
  }

  $grid.queryByExamplePage = queryByExamplePage
  getGridTreeOptions($grid).queryByExamplePage = queryByExamplePage

  $grid.loadTreeData = async (flatList) => {
    const { reactData } = $grid
    reactData.loading = false
    reactData.loadError = false
    reactData.loadErrorMsg = ''
    reactData.currentPage = 1
    reactData.total = flatList.length
    await applyFlatList(flatList)
  }
}
